import { useState } from 'react';

// Палитра для строк МРЧ — красный/коралловый зарезервированы под пересечения
const ROW_COLORS = ['#378ADD', '#1D9E75', '#7F77DD', '#BA7517', '#5F5E5A', '#D4537E'];
const OVERLAP_COLOR = '#E24B4A';
const PENDING_COLOR = '#B4B2A9';

const DOW = ['ВС','ПН','ВТ','СР','ЧТ','ПТ','СБ'];

/**
 * routes: [{ id, internal_code, merchandisers }]
 * vacations: [{ route_id, date_from, date_to, status }]
 * onOpenRoute(routeId): переход на строку с отпуском этого МРЧ
 * daysInMonth, offset: смещение первого дня месяца в неделе (для сентября 2026 offset=1, т.к. 1 сен = вторник)
 */
export default function VacationCalendar({ routes, vacations, onOpenRoute, daysInMonth = 30, offset = 1, monthPrefix }) {
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  function dow(d) { return (offset + d - 1) % 7; }
  function isWeekend(d) { const w = dow(d); return w === 0 || w === 6; }

  // #2 — обрезаем диапазон отпуска по видимому месяцу (переходящий отпуск рисуется корректно)
  const monthStart = monthPrefix ? `${monthPrefix}-01` : null;
  const monthEnd   = monthPrefix ? `${monthPrefix}-${String(daysInMonth).padStart(2, '0')}` : null;
  function clampRange(v) {
    let from, to;
    if (monthStart) {
      from = v.date_from < monthStart ? 1 : parseInt(v.date_from.slice(8, 10), 10);
      to   = v.date_to  > monthEnd   ? daysInMonth : parseInt(v.date_to.slice(8, 10), 10);
    } else {
      from = parseInt(v.date_from.split('-')[2], 10);
      to   = parseInt(v.date_to.split('-')[2], 10);
    }
    return { from, to };
  }

  function getMrc(route) {
    const m = route?.merchandisers;
    if (!m) return null;
    return Array.isArray(m) ? m[0] : m;
  }

  // Только не отклонённые (approved + pending), показываем строку
  const rows = routes
    .filter(r => !r.is_vacancy)
    .map((route, idx) => {
      const v = vacations.find(vv => vv.route_id === route.id && vv.status !== 'rejected');
      const mrc = getMrc(route);
      return v ? { route, mrc, vacation: v, color: ROW_COLORS[idx % ROW_COLORS.length] } : null;
    })
    .filter(Boolean);

  if (!rows.length) {
    return (
      <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 13 }}>
        Нет запланированных отпусков
      </div>
    );
  }

  // Считаем пересечения по дням — только среди approved
  const dayOverlapCount = {};
  days.forEach(d => { dayOverlapCount[d] = 0; });
  rows.filter(r => r.vacation.status === 'approved').forEach(r => {
    const { from, to } = clampRange(r.vacation);
    for (let d = from; d <= to; d++) if (dayOverlapCount[d] != null) dayOverlapCount[d]++;
  });

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, fontSize: 11, color: 'var(--text-muted)', marginBottom: 8, justifyContent: 'flex-end' }}>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: OVERLAP_COLOR, marginRight: 4 }} />пересечение</span>
        <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: PENDING_COLOR, marginRight: 4 }} />не подтверждено</span>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 10 }}>
        <div style={{ minWidth: 130 + daysInMonth * 22 }}>
          {/* Заголовок с числами дней */}
          <div style={{ display: 'grid', gridTemplateColumns: `130px repeat(${daysInMonth}, 22px)`, borderBottom: '1px solid var(--border)' }}>
            <div style={{ padding: '6px 8px', fontSize: 10, color: 'var(--text-light)', position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 2 }}>МРЧ</div>
            {days.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 9, color: isWeekend(d) ? 'var(--text-light)' : 'var(--text-muted)', padding: '4px 0' }}>{d}</div>
            ))}
          </div>

          {/* Строки */}
          {rows.map(({ route, mrc, vacation, color }) => {
            const { from, to } = clampRange(vacation);
            const isPending = vacation.status === 'pending';

            return (
              <div
                key={route.id}
                onClick={() => onOpenRoute?.(route.id)}
                style={{
                  display: 'grid', gridTemplateColumns: `130px repeat(${daysInMonth}, 22px)`,
                  alignItems: 'center', borderBottom: '1px solid var(--border)', cursor: 'pointer',
                }}
              >
                <div style={{
                  padding: '8px', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6,
                  color: isPending ? 'var(--text-muted)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1,
                }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: isPending ? PENDING_COLOR : color, flexShrink: 0 }} />
                  {mrc?.full_name}
                </div>
                {days.map(d => {
                  const inRange = d >= from && d <= to;
                  let bg = 'transparent';
                  if (inRange) {
                    if (isPending) bg = PENDING_COLOR;
                    else if (dayOverlapCount[d] >= 2) bg = OVERLAP_COLOR;
                    else bg = color;
                  }
                  return <div key={d} style={{ height: 16, margin: '6px 1px', borderRadius: 3, background: bg }} />;
                })}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
        Нажмите на строку, чтобы перейти к отпуску сотрудника
      </div>
    </div>
  );
}
